import { FormEvent, useState } from 'react';
import type { ExpenseCostSplit } from '../../api';
import { Aircraft, Issue, IssueAttachment, User } from '../../types';
import { useFeedback } from '../../hooks/useFeedback';
import { useSubmitGuard } from '../../hooks/useSubmitGuard';
import {
  formatIssuePerformerLabel,
  issuePerformerError,
  issuePerformerFromSaved,
} from '../../utils/aircraftMaintenance';
import { isLegacyRepairSplit, parseIssueCost } from '../../utils/issueDisplay';
import { hoursFlightIdsPayload, manualShareError, manualSharePairs, sharesFromSaved } from '../../utils/expenseSplit';
import ExpenseSplitFields from '../ExpenseSplitFields/ExpenseSplitFields';
import IssueAttachmentsBlock from '../IssueAttachmentsBlock/IssueAttachmentsBlock';
import IssueResolveFields from '../IssueResolveFields/IssueResolveFields';
import ModalPortal from '../modal/ModalPortal';
import '../modal/ModalBase.css';

const todayIso = () => new Date().toISOString().split('T')[0];

const toDateInput = (value?: string | null) => {
  if (!value) return todayIso();
  const text = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(text)) return text.slice(0, 10);
  return todayIso();
};

const repairSplit = (value?: string | null): ExpenseCostSplit => {
  if (value === 'manual' || value === 'hours' || value === 'personal') {
    return value;
  }
  return 'shares';
};

export type EditIssuePayload = {
  description: string;
  severity: string;
  issue_date: string;
  resolved_date?: string;
  resolved_by?: string;
  cost?: number;
  cost_split?: ExpenseCostSplit;
  payer_ids?: number[];
  payer_shares?: number[];
  flight_ids?: number[];
  comment?: string;
};

type Props = {
  issue: Issue;
  aircraft?: Aircraft | null;
  members: User[];
  currentUserId?: number;
  canEditResolution?: boolean;
  filesUploading?: boolean;
  onClose: () => void;
  onConfirm: (data: EditIssuePayload) => void | Promise<void>;
  onOpenFile?: (file: IssueAttachment, files: IssueAttachment[]) => void;
  onDeleteFile?: (file: IssueAttachment) => void;
  onAddFiles?: (files: File[]) => void;
};

const EditIssueModal = ({
  issue,
  aircraft,
  members,
  currentUserId = 0,
  canEditResolution = false,
  filesUploading = false,
  onClose,
  onConfirm,
  onOpenFile,
  onDeleteFile,
  onAddFiles,
}: Props) => {
  const { showError, feedbackNode } = useFeedback();
  const { submitting, runSubmit } = useSubmitGuard();
  const fromFlight = Boolean(issue.flight_id);
  const resolved = Boolean(issue.is_resolved);
  const initialPerformer = issuePerformerFromSaved(issue.resolved_by, aircraft);
  const initialSplit = repairSplit(issue.cost_split);
  const legacySplit = isLegacyRepairSplit(issue.cost_split);
  const [form, setForm] = useState({
    description: issue.description || '',
    severity: issue.severity || 'medium',
    issue_date: toDateInput(issue.issue_date || issue.flight_date),
    resolved_date: toDateInput(issue.resolved_at),
    performerType: initialPerformer.type,
    performerName: initialPerformer.name,
    cost: issue.cost != null ? String(issue.cost) : '0',
    cost_split: initialSplit,
    payer_user_id: issue.payer_ids?.[0] || currentUserId,
    comment: issue.comment || '',
  });
  const [manualShares, setManualShares] = useState<Record<number, string>>(() =>
    sharesFromSaved(issue.payer_ids || [], issue.payer_shares)
  );
  const [flightIds, setFlightIds] = useState<number[]>(() => issue.flight_ids || []);

  const amountValue = Number(String(form.cost).replace(',', '.'));
  const previewAmount =
    form.cost !== '' && Number.isFinite(amountValue) && amountValue >= 0
      ? amountValue
      : null;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    if (!form.description.trim()) {
      showError('Укажите описание');
      return;
    }
    if (!form.issue_date) {
      showError('Укажите дату неисправности');
      return;
    }
    if (!fromFlight && form.issue_date > todayIso()) {
      showError('Дата неисправности не может быть позже сегодняшнего дня');
      return;
    }

    const payload: EditIssuePayload = {
      description: form.description.trim(),
      severity: form.severity || 'medium',
      issue_date: form.issue_date,
      comment: form.comment.trim(),
    };

    if (resolved && canEditResolution) {
      if (!form.resolved_date) {
        showError('Укажите дату устранения');
        return;
      }
      const performerError = issuePerformerError(
        form.performerType,
        form.performerName
      );
      if (performerError) {
        showError(performerError);
        return;
      }
      const parsed = parseIssueCost(form.cost);
      if (!parsed.ok) {
        showError(parsed.error);
        return;
      }
      if (legacySplit) {
        if (form.cost_split === 'hours' && flightIds.length === 0) {
          showError('Выберите полёты для пропорционального расчёта');
          return;
        }
        if (form.cost_split === 'manual') {
          const error = manualShareError(manualShares);
          if (error) {
            showError(error);
            return;
          }
        }
        const pairs = manualSharePairs(manualShares);
        payload.resolved_date = form.resolved_date;
        payload.resolved_by = formatIssuePerformerLabel(
          form.performerType,
          form.performerName
        );
        payload.cost = parsed.value;
        payload.cost_split = form.cost_split;
        payload.payer_ids =
          form.cost_split === 'manual' ? pairs.map((row) => row.user_id) : undefined;
        payload.payer_shares =
          form.cost_split === 'manual' ? pairs.map((row) => row.share) : undefined;
        payload.flight_ids = hoursFlightIdsPayload(
          form.cost_split,
          flightIds,
          issue.flight_ids
        );
      } else {
        const paid = parsed.value > 0;
        const split: ExpenseCostSplit =
          paid && form.cost_split === 'personal' ? 'personal' : 'shares';
        if (split === 'personal' && !form.payer_user_id) {
          showError('Укажите, кто оплатит устранение');
          return;
        }
        payload.resolved_date = form.resolved_date;
        payload.resolved_by = formatIssuePerformerLabel(
          form.performerType,
          form.performerName
        );
        payload.cost = parsed.value;
        payload.cost_split = split;
        payload.payer_ids =
          split === 'personal' ? [form.payer_user_id] : undefined;
      }
    }

    void runSubmit(() => onConfirm(payload));
  };

  return (
    <ModalPortal>
      <div className="modal-overlay" onClick={onClose}>
        {feedbackNode}
        <form
          className="modal"
          onClick={(e) => e.stopPropagation()}
          onSubmit={handleSubmit}
        >
          <h3>Изменить неисправность</h3>
          <div className="modal-body">
            <label>Описание</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              required
            />
            <label>Степень важности</label>
            <select
              value={form.severity}
              onChange={(e) => setForm({ ...form, severity: e.target.value })}
            >
              <option value="low">Низкая</option>
              <option value="medium">Средняя</option>
              <option value="high">Высокая</option>
            </select>
            <label>Дата неисправности</label>
            <input
              type="date"
              value={form.issue_date}
              onChange={(e) => setForm({ ...form, issue_date: e.target.value })}
              max={todayIso()}
              required
              disabled={fromFlight}
            />
            {fromFlight ? (
              <p>Дата взята из полёта и не меняется</p>
            ) : null}
            <label>Комментарий</label>
            <textarea
              value={form.comment}
              onChange={(e) => setForm({ ...form, comment: e.target.value })}
              placeholder="Любая информация по этой неисправности..."
              rows={5}
            />
            {onOpenFile && onAddFiles && onDeleteFile ? (
              <IssueAttachmentsBlock
                files={issue.attachments || []}
                canEdit
                uploading={filesUploading}
                onOpen={onOpenFile}
                onDelete={onDeleteFile}
                onAdd={onAddFiles}
              />
            ) : null}

            {resolved && canEditResolution ? (
              <>
                <label>Дата устранения</label>
                <input
                  type="date"
                  value={form.resolved_date}
                  onChange={(e) =>
                    setForm({ ...form, resolved_date: e.target.value })
                  }
                />
                <IssueResolveFields
                  members={members}
                  performerType={form.performerType}
                  performerName={form.performerName}
                  cost={form.cost}
                  costSplit={form.cost_split}
                  payerUserId={form.payer_user_id}
                  onPerformerTypeChange={(performerType) =>
                    setForm({ ...form, performerType })
                  }
                  onPerformerNameChange={(performerName) =>
                    setForm({ ...form, performerName })
                  }
                  onCostChange={(cost) => setForm({ ...form, cost })}
                  onCostSplitChange={(cost_split) =>
                    setForm({
                      ...form,
                      cost_split,
                      payer_user_id:
                        cost_split === 'personal'
                          ? form.payer_user_id || currentUserId
                          : form.payer_user_id,
                    })
                  }
                  onPayerUserIdChange={(payer_user_id) =>
                    setForm({ ...form, payer_user_id })
                  }
                  legacySplit={legacySplit}
                />
                {legacySplit ? (
                  <ExpenseSplitFields
                    date={form.resolved_date}
                    amount={previewAmount}
                    expenseType="repair"
                    costSplit={form.cost_split}
                    manualShares={manualShares}
                    flightIds={flightIds}
                    onCostSplitChange={(value) =>
                      setForm({ ...form, cost_split: value })
                    }
                    onManualSharesChange={setManualShares}
                    onFlightIdsChange={setFlightIds}
                  />
                ) : null}
              </>
            ) : null}
          </div>
          <div className="modal-actions">
            <button
              type="button"
              className="btn-cancel"
              onClick={onClose}
              disabled={submitting}
            >
              Отмена
            </button>
            <button type="submit" className="btn-save" disabled={submitting}>
              {submitting ? '…' : 'Сохранить'}
            </button>
          </div>
        </form>
      </div>
    </ModalPortal>
  );
};

export default EditIssueModal;
